import * as THREE from 'three';

export const labelBuilder = (text, size, position = { x: 0, y: 20, z: 0 }, backgroundColor, scene = null) => {
    const createTextTexture = (text, fontSize = 50, textColor, backgroundColor) => {
        // Tenir compte du pixel ratio pour une meilleure netteté
        const pixelRatio = window.devicePixelRatio || 1;
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Définir une police plus grande pour plus de netteté
        const scaledFontSize = fontSize * pixelRatio;
        ctx.font = `bold ${scaledFontSize}px Arial`;
        
        // Mesurer le texte avec la nouvelle police
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = scaledFontSize * 1.2; // Ajout d'un peu d'espace vertical
        
        // Calculer un diamètre qui garantit que le texte tient bien
        const padding = 30 * pixelRatio;
        const diameter = Math.max(textWidth, textHeight) + padding;
        
        // Définir une taille de canvas en tenant compte du pixel ratio
        canvas.width = diameter;
        canvas.height = diameter;
        
        // Améliorer le rendu du texte
        ctx.textRendering = 'optimizeLegibility';
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Dessiner le cercle noir avec antialiasing
        ctx.beginPath();
        ctx.arc(diameter/2, diameter/2, (diameter/2) - 2, 0, Math.PI * 2);
        ctx.fillStyle = backgroundColor;
        ctx.fill();
        
        // Centrer et dessiner le texte
        ctx.font = `bold ${scaledFontSize}px Arial`;
        ctx.fillStyle = textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, diameter/2, diameter/2);
        
        // Créer la texture
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter; // Améliore la netteté
        texture.generateMipmaps = false; // Désactiver les mipmaps pour plus de netteté
        
        return {
            texture,
            width: canvas.width,
            height: canvas.height
        };
    };

    const { texture, width, height } = createTextTexture(text, 50, getContrastColor(backgroundColor), backgroundColor);
    const spriteMaterial = new THREE.SpriteMaterial({ 
        map: texture,
        transparent: true,
        sizeAttenuation: true // S'assurer que la taille est conservée à distance
    });
    
    const textSprite = new THREE.Sprite(spriteMaterial);
    
    // Conserver le ratio d'aspect du canvas pour éviter la déformation
    const aspectRatio = width / height;
    textSprite.scale.set(size * aspectRatio, size, 1);
    textSprite.position.set(position.x, position.y, position.z);
    textSprite.size = size;
    
    scene.add(textSprite);
    
    // Ajouter une méthode dispose pour nettoyer les ressources
    textSprite.dispose = () => {
        if (textSprite.material) {
            if (textSprite.material.map) {
                textSprite.material.map.dispose();
            }
            textSprite.material.dispose();
        }
        scene.remove(textSprite);
    };
    
    return textSprite;
};

export const defaultScale = (label) => {
    if (!label.size || !label.scale) return;
    // Conserver le ratio d'aspect original
    const aspectRatio = label.scale.x / label.scale.y;
    label.scale.set(1.2 * label.size * aspectRatio, 1.2 * label.size, 1);
}

export const bigScale = (label) => {
    if (!label.size || !label.scale) return;
    // Conserver le ratio d'aspect original
    const aspectRatio = label.scale.x / label.scale.y;
    label.scale.set(2.4 * label.size * aspectRatio, 2.4 * label.size, 1);
}

const getContrastColor = (bgColor) => {
    // Enlever le # si présent
    const hexColor = bgColor.startsWith('#') ? bgColor.slice(1) : bgColor;
    
    // Convertir en RGB
    const r = parseInt(hexColor.substr(0, 2), 16);
    const g = parseInt(hexColor.substr(2, 2), 16);
    const b = parseInt(hexColor.substr(4, 2), 16);
    
    // Calculer la luminosité (formule standard)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Retourner blanc ou noir selon la luminosité du fond
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
};